import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useCheckUsername } from '@modules/auth/hooks/useCheckUsername';

import type { CreateUsernameFormType, UsernameStatusType } from './types';

type VerifyUsernameProps = {
  formik: CreateUsernameFormType;
  setUsernameStatus: (value: UsernameStatusType) => void;
};

const VerifyUsername: FC<VerifyUsernameProps> = ({
  formik,
  setUsernameStatus,
}) => {
  const { t } = useTranslation();
  const username = formik.values.username;
  const debouncedUsername = useDebouncedValue(username, 400);
  const { available, checking } = useCheckUsername(debouncedUsername);

  useEffect(() => {
    const isTyping = username !== debouncedUsername;

    if (isTyping || checking) {
      setUsernameStatus('checking');
      return;
    }

    if (username.length < 3 || !!formik.errors.username) {
      setUsernameStatus('idle');
      return;
    }

    if (available === false) {
      setUsernameStatus('taken');
      formik.setFieldError(
        'username',
        t('auth.create_username.unavailable', 'Username is not available.')
      );
    }

    if (available === true) {
      setUsernameStatus('available');
      formik.setFieldError('username', undefined);
    }
  }, [
    available,
    checking,
    username,
    debouncedUsername,
    formik.errors.username,
  ]);

  return null;
};

export { VerifyUsername };
